<!--
  CallConnectionLookup.vue
  콜봇 월 정산 · 연결 진단 탭의 "통화 1건 연결 확인"(화면 명세 2-6, 7차 개정에서 더함).

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2-6 › 통화 1건 연결 확인", "요청 계약 6"(통화 단건 재사용)
  - 통화 ID 를 넣고 [확인] → GET .../callbot/pbx-calls/:voxCallId(이미 있는 통화 단건). 확인할 때마다 부른다.
  - 보이는 것: 결과(응답 / 미응답 — 연결 시각이 있거나 통화 시간이 0보다 크면 응답), 연결 경로, 연결 대상 번호
    (끝 4자리), 수신 시각, 벨·대기 시간(연결 − 수신, 연결이 없으면 종료 − 수신), 직원 통화 시간.
    판정·표기는 settlementRules.pbxCallSummary 를 쓴다.
  - 연결 시도 순서(단건 응답 `attempts` — ~/md/front/콜봇-연결시도-attempts-call-api구현회신.md admin 4): 받은 순서
    그대로 후보 번호마다 지시·결과·벨 시간과 진단 원값(DialStatus · Q.850 cause · SIP)을 보인다. null(키가 없는 옛 응답
    포함)은 수집 전, 빈 배열은 연결 시도 기록 없음이다.
  - 고객 번호(caller_number)는 응답에 실려 와도 상태에 두지 않고 그리지 않는다(화면 명세 4).
  - 404(PBX_CALL_NOT_FOUND) = 상담사 연결 leg 없음(콜봇 단독 통화). 403 = call-api 가 이 GET 을 아직
    CERTILIFE 에 열지 않았다(요청 측 현재 상태 — 열리면 문구 없이 동작한다).
  - 부모(DiagTab)는 미응답 목록 행을 누르면 노출한 lookup(id) 로 이 칸을 채워 바로 확인한다.
  - 목이면 응답·미응답 견본 통화 ID 를 눌러 볼 수 있게 보인다(응답 통화 ID 는 목 밖에서 알 길이 없다).
-->
<template>
  <section class="lookup-card" :aria-labelledby="titleId">
    <header class="lookup-card__head">
      <h3 :id="titleId" class="lookup-card__title">통화 1건 연결 확인</h3>
      <form class="lookup-card__form" role="search" @submit.prevent="onSubmit">
        <a-input
          v-model:value="inputId"
          class="lookup-card__input"
          allow-clear
          placeholder="통화 ID(vox_call_id)"
          aria-label="통화 ID"
          :disabled="loading"
        />
        <a-button type="primary" class="yellow-button" html-type="submit" :loading="loading">확인</a-button>
      </form>
    </header>
    <p class="lookup-card__desc">
      통화 ID 로 그 통화의 상담사 연결 기록(PBX·vox 직접)과 후보 번호마다 울린 순서를 봅니다. 미응답 목록의 행을 눌러도
      열립니다.
    </p>

    <SettlementMockBadge v-if="mock && (samples.answered.length || samples.unanswered.length)">
      <span>견본 통화</span>
      <button
        v-for="sample in sampleButtons"
        :key="sample.id"
        type="button"
        class="lookup-card__sample"
        :title="sample.id"
        @click="lookup(sample.id)"
      >
        {{ sample.label }}
      </button>
    </SettlementMockBadge>

    <div v-if="result" class="lookup-result" aria-live="polite">
      <template v-if="result.kind === 'found'">
        <div class="lookup-result__head">
          <span class="lookup-result__badge" :class="result.summary.answered ? 'is-answered' : 'is-missed'">
            {{ result.summary.resultLabel }}
          </span>
          <code class="lookup-result__id">{{ result.id }}</code>
        </div>
        <dl class="lookup-facts">
          <div v-for="fact in facts" :key="fact.key" class="lookup-facts__item">
            <dt>{{ fact.label }}</dt>
            <dd>{{ fact.value }}</dd>
            <span v-if="fact.note" class="lookup-facts__note">{{ fact.note }}</span>
          </div>
        </dl>

        <!-- 연결 시도 순서 — null 은 수집 전, [] 는 기록 없음 -->
        <div class="lookup-attempts">
          <div class="lookup-attempts__head">
            <span class="lookup-attempts__title">연결 시도 순서</span>
            <span v-if="result.summary.attempts && result.summary.attempts.length" class="lookup-attempts__meta">
              {{ result.summary.attempts.length }}건 · 받은 순서 그대로 · 시작은 직원 연결 요청 뒤 경과
            </span>
          </div>
          <p v-if="result.summary.attempts === null" class="lookup-attempts__empty">{{ ATTEMPTS_PENDING_TEXT }}</p>
          <p v-else-if="!result.summary.attempts.length" class="lookup-attempts__empty">
            연결 시도 기록 없음<template v-if="result.summary.voxDirect"> — {{ VOX_DIRECT_NO_ATTEMPTS_TEXT }}</template>
          </p>
          <div v-else class="lookup-attempts__wrap">
            <table class="lookup-attempts__table">
              <thead>
                <tr>
                  <th scope="col" class="is-num">순서</th>
                  <th scope="col" class="is-num">시작</th>
                  <th scope="col">번호</th>
                  <th scope="col">후보</th>
                  <th scope="col">지시</th>
                  <th scope="col">결과</th>
                  <th scope="col" class="is-num">벨</th>
                  <th scope="col">진단</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="a in result.summary.attempts" :key="a.key">
                  <td class="is-num">{{ a.order }}</td>
                  <td class="is-num">{{ a.startedAt }}</td>
                  <td class="is-num">{{ a.number }}</td>
                  <td class="is-num">{{ a.candidate }}</td>
                  <td>{{ a.trigger }}</td>
                  <td>
                    <span class="lookup-attempts__outcome" :class="{ 'is-answered': a.answered }">{{ a.outcomeLabel }}</span>
                    <code v-if="a.outcomeCode" class="lookup-attempts__code">{{ a.outcomeCode }}</code>
                  </td>
                  <td class="is-num">{{ a.ring }}</td>
                  <td class="lookup-attempts__diag">{{ a.diagnostics }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-if="result.summary.attempts && result.summary.attempts.length" class="lookup-attempts__foot">
            {{ ATTEMPTS_DIAG_NOTE }}
          </p>
        </div>
      </template>

      <div v-else-if="result.kind === 'none'" class="lookup-result__state">
        <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
        <span>{{ PBX_CALL_NOT_FOUND_TEXT }}</span>
        <code class="lookup-result__id">{{ result.id }}</code>
      </div>

      <a-alert v-else :type="result.tone" show-icon :message="result.text" />
    </div>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { message } from 'ant-design-vue';

import SettlementMockBadge from '../SettlementMockBadge.vue';
import {
  getCallApiErrorStatus,
  isCallApiUnimplemented,
  parseCallApiError,
} from '@/views/Callbot/service/callApiError';
import { PBX_CALL_NOT_FOUND_TEXT, pbxCallSummary } from '../../service/settlementRules';
import { settlementMockSampleCallIds } from '../../service/settlementMock';

const props = defineProps({
  companyId: { type: String, required: true },
  /** 'YYYY-MM' — 목 견본 통화 ID 를 고를 때만 쓴다 */
  month: { type: String, required: true },
  /** settlementApi 또는 같은 모양의 목 */
  api: { type: Object, required: true },
  mock: { type: Boolean, default: false },
});

/** 단건 `attempts: null` — 시도를 모른다(이 환경에 시도 기록이 아직 없거나 수집 시작 전에 들어온 통화) */
const ATTEMPTS_PENDING_TEXT =
  '수집 전 — 이 환경에 연결 시도 기록이 아직 없거나, 시도 수집을 시작하기 전에 들어온 통화입니다.';
const VOX_DIRECT_NO_ATTEMPTS_TEXT = 'vox 직접 연결은 우리 PBX 가 상담사 번호를 울리지 않아 시도가 남지 않습니다.';
const ATTEMPTS_DIAG_NOTE =
  '결과는 pbx-ami outcome 으로만 판정합니다. 진단 칸은 DialStatus · Q.850 cause · SIP 원값이라 결과와 어긋나 보여도 정상일 수 있습니다.';
const FORBIDDEN_TEXT = 'call-api 가 이 조회를 운영(CERTILIFE) 계정에 아직 열지 않았습니다.';
const UNIMPLEMENTED_TEXT = '통화 단건 조회 API 가 이 환경에 아직 없습니다.';

const titleId = `st-call-lookup-${getCurrentInstance()?.uid ?? 0}`;

const inputId = ref('');
const loading = ref(false);
/**
 * 마지막 확인 결과. 응답 원문은 두지 않는다(고객 번호가 실려 온다).
 * kind: 'found'(summary = pbxCallSummary) | 'none'(404 — 상담사 leg 없음) | 'error'(tone·text)
 * @type {import('vue').Ref<null | { kind: string, id: string, summary?: object, tone?: string, text?: string }>}
 */
const result = ref(null);
// 늦게 온 앞 응답이 새 결과를 덮지 않게 — 마지막 요청만 반영
let seq = 0;

const facts = computed(() => {
  const s = result.value?.kind === 'found' ? result.value.summary : null;
  if (!s) return [];
  return [
    { key: 'route', label: '연결 경로', value: s.routeLabel, note: '' },
    { key: 'target', label: '연결 대상 번호', value: s.target, note: s.answered ? '받은 번호' : '연결하려 한 번호' },
    { key: 'received', label: '수신 시각', value: s.receivedAt, note: 'KST · 직원 연결 요청 시각' },
    { key: 'wait', label: '벨·대기 시간', value: s.wait, note: s.answered ? '수신 → 연결' : '수신 → 종료' },
    { key: 'talk', label: '직원 통화 시간', value: s.talk, note: s.answered ? '' : '받은 직원 없음' },
  ];
});

function describeError(err, id) {
  const status = getCallApiErrorStatus(err);
  // 라우트 없음(미구현) 404 와 "그 통화의 상담사 leg 없음" 404(PBX_CALL_NOT_FOUND)를 가른다
  if (isCallApiUnimplemented(err)) return { kind: 'error', id, tone: 'warning', text: UNIMPLEMENTED_TEXT };
  if (status === 404) return { kind: 'none', id };
  if (status === 403) return { kind: 'error', id, tone: 'warning', text: FORBIDDEN_TEXT };
  return { kind: 'error', id, tone: 'error', text: parseCallApiError(err, '통화 연결 기록을 불러오지 못했습니다.') };
}

/**
 * 통화 1건 확인. 부모가 미응답 행을 눌렀을 때도 부른다.
 * @param {string} [rawId] 없으면 입력 칸 값
 */
async function lookup(rawId) {
  const id = String(rawId ?? inputId.value ?? '').trim();
  if (!id) {
    message.warning('통화 ID 를 입력하세요.');
    return;
  }
  inputId.value = id;
  const my = ++seq;
  loading.value = true;
  try {
    const res = await props.api.getPbxCall(props.companyId, id);
    if (my !== seq) return;
    result.value = { kind: 'found', id, summary: pbxCallSummary(res) };
  } catch (err) {
    if (my !== seq) return;
    result.value = describeError(err, id);
  } finally {
    if (my === seq) loading.value = false;
  }
}

function onSubmit() {
  void lookup();
}

defineExpose({ lookup });

// ---------------------------------------------------------------------------
// 목 전용 — 견본 통화 ID
// ---------------------------------------------------------------------------

const samples = computed(() =>
  props.mock ? settlementMockSampleCallIds(props.companyId, props.month) : { answered: [], unanswered: [] }
);

const sampleButtons = computed(() => [
  ...samples.value.answered.map((id, i) => ({ id, label: `응답 ${i + 1}` })),
  ...samples.value.unanswered.map((id, i) => ({ id, label: `미응답 ${i + 1}` })),
]);
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

@include yellow-button;

.lookup-card {
  @include st-card;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
  }

  &__title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: $st-text;
  }

  &__form {
    display: flex;
    gap: 8px;
    margin-left: auto;
    min-width: 0;
  }

  &__input {
    width: 320px;
    max-width: 100%;
  }

  &__desc {
    margin: 0;
    font-size: 12px;
    color: $st-muted;
  }

  &__sample {
    height: auto; // 전역 button { height: 40px } 덮어쓰기
    padding: 0 8px;
    border: 1px solid $st-warning-border;
    border-radius: 999px;
    background: #fff;
    color: $st-warning;
    font-size: 11.5px;
    line-height: 20px;
    cursor: pointer;

    &:hover {
      background: $st-warning-bg;
    }
  }
}

.lookup-result {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid $st-border;
  border-radius: 10px;
  background: $st-bg;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  &__badge {
    @include st-pill;
    font-size: 12px;

    &.is-answered {
      @include st-pill-tone($st-ok, $st-ok-bg, $st-ok-border);
    }

    &.is-missed {
      @include st-pill-tone($st-warning, $st-warning-bg, $st-warning-border);
    }
  }

  &__id {
    color: $st-text-secondary;
    font-size: 12px;
    word-break: break-all;
  }

  &__state {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
    font-size: 13px;
    color: $st-text;
  }
}

.lookup-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 150px), 1fr));
  gap: 8px;
  margin: 0;

  &__item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #fff;

    dt {
      font-size: 11.5px;
      font-weight: 400;
      color: $st-muted;
    }

    dd {
      @include st-num;
      margin: 0;
      font-size: 14px;
      font-weight: 700;
      color: $st-text;
    }
  }

  &__note {
    font-size: 11px;
    color: $st-muted;
  }
}

.lookup-attempts {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 10px;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: $st-text;
  }

  &__meta,
  &__foot {
    margin: 0;
    font-size: 11.5px;
    color: $st-muted;
  }

  &__empty {
    margin: 0;
    padding: 8px 10px;
    border-radius: 8px;
    background: #fff;
    font-size: 12.5px;
    color: $st-text-secondary;
  }

  &__wrap {
    overflow-x: auto;
    border: 1px solid $st-border;
    border-radius: 8px;
    background: #fff;
  }

  &__table {
    width: 100%;
    min-width: 720px;
    border-collapse: collapse;
    font-size: 12.5px;

    th,
    td {
      padding: 6px 10px;
      // !important — CertiAccManage.vue 의 전역 td { border-bottom: transparent !important } 가 td 줄을 지워 항목 칸(th)에만 줄이 남는다
      border-bottom: 1px solid $st-border !important;
      text-align: left;
      white-space: nowrap;
    }

    thead th {
      background: $st-bg;
      font-size: 12px;
      font-weight: 600;
      color: $st-text-secondary;
    }

    tbody tr:last-child td {
      border-bottom: 0 !important;
    }

    .is-num {
      @include st-num;
    }
  }

  &__outcome {
    font-weight: 600;
    color: $st-warning;

    &.is-answered {
      color: $st-ok;
    }
  }

  &__code {
    margin-left: 4px;
    padding: 0 4px;
    border-radius: 4px;
    background: #f5f5f5;
    color: $st-text-secondary;
    font-size: 11px;
  }

  &__diag {
    color: $st-text-secondary;
    font-size: 12px;
  }
}
</style>
